import { useState } from "react";
import { useTranslation } from 'react-i18next';
import SingleCustomCollectionTableRow from "./SingleCustomCollectionTableRow";
import NoResultsIndicator from "@/widgets/NoResultsIndicator";
import ColumnFilterModal from "./ColumnFilterModal";
import { getSortIndicator, getFilterIndicator } from "@/lib/tableHelpers";

export default function SingleCustomCollectionTable({
  items,
  collectionDetails,
  loading,
  selectedItems,
  onSelectAll,
  onSelectItem,
  isAllSelected,
  isIndeterminate,
  sortColumn,
  sortDirection,
  onSort,
  collectionId,
  filters,
  onApplyFilter,
  lookupOptions = {}
}) {
  const { t } = useTranslation();
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const handleFilterClick = (field) => {
    setSelectedField(field);
    setFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false);
    setSelectedField(null);
  };

  const hasFilter = (fieldName) => {
    return filters && filters[fieldName] && filters[fieldName] !== null;
  };

  // All fields are displayable and sortable
  const displayFields = collectionDetails?.fields?.filter(field => field.name !== 'collection') || [];

  return (
    <div className="mt-6 overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow">
        <thead>
          <tr>
            <th className="py-2 px-4 border-b border-gray-300 text-left w-12">
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(el) => {
                  if (el) el.indeterminate = isIndeterminate;
                }}
                onChange={(e) => onSelectAll(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
            </th>
            
            {/* Dynamic fields based on collection structure - all sortable */}
            {displayFields.map((field) => (
              <th key={field.name} className="py-2 px-4 border-b border-gray-300 text-left">
                <div className="flex items-center justify-between">
                  <button
                    className="flex items-center font-semibold text-gray-700 hover:text-blue-600 transition-colors duration-200 focus:outline-none focus:text-blue-600"
                    onClick={() => onSort(field.name)}
                  >
                    {field.label || field.name}
                    {getSortIndicator(field.name, sortColumn, sortDirection)}
                  </button>
                  <button
                    className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
                    onClick={() => handleFilterClick(field)}
                    title={t('singleCustomCollection.list.table.filterColumn')}
                  >
                    {getFilterIndicator(hasFilter(field.name))}
                  </button>
                </div>
              </th>
            ))}

            {/* Created date column */}
            <th className="py-2 px-4 border-b border-gray-300 text-left">
              <div className="flex items-center justify-between">
                <button
                  className="flex items-center font-semibold text-gray-700 hover:text-blue-600 transition-colors duration-200 focus:outline-none focus:text-blue-600"
                  onClick={() => onSort('created')}
                >
                  {t('singleCustomCollection.list.table.created')}
                  {getSortIndicator('created', sortColumn, sortDirection)}
                </button>
                <button
                  className="ml-2 p-1 hover:bg-gray-100 rounded transition-colors duration-200 focus:outline-none focus:bg-gray-100"
                  onClick={() => handleFilterClick({ name: 'created', type: 'date', label: t('singleCustomCollection.list.table.created') })}
                  title={t('singleCustomCollection.list.table.filterColumn')}
                >
                  {getFilterIndicator(hasFilter('created'))}
                </button>
              </div>
            </th>

            <th className="py-2 px-4 border-b border-gray-300 text-right w-28">
              <span className="font-semibold text-gray-700">{t('singleCustomCollection.list.table.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && !loading ? (
            <tr>
              <td colSpan={displayFields.length + 3} className="py-8">
                <NoResultsIndicator
                  message={t('singleCustomCollection.list.emptyState.title')}
                  description={t('singleCustomCollection.list.emptyState.description')}
                />
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <SingleCustomCollectionTableRow
                key={item._id}
                item={item}
                collectionDetails={collectionDetails}
                collectionId={collectionId}
                displayFields={displayFields}
                isSelected={selectedItems.has(item._id)}
                onSelectItem={onSelectItem}
                lookupOptions={lookupOptions}
              />
            ))
          )}
        </tbody>
      </table>

      {/* Filter Modal */}
      <ColumnFilterModal
        isOpen={filterModalOpen}
        onClose={handleCloseFilterModal}
        field={selectedField}
        currentFilter={selectedField ? filters?.[selectedField.name] : null}
        onApplyFilter={onApplyFilter}
      />
    </div>
  );
}
